import { BaseClient } from '../base-client.js';
import { BASE_URLS } from '../../config';
import Utils from '../../helpers/utils';
import RequestResponseHelpers from '../../helpers/RequestResponseHelpers';

class NhstAuthClient extends BaseClient {
  constructor(config) {
    super(config);
    this.baseUrl = this.baseUrl || BASE_URLS.NHST_AUTH_API;
    if (this.config.BASE_URL_PREFIX) {
      this.baseUrl = this.config.BASE_URL_PREFIX + this.baseUrl;
    }
    if (this.isServer) {
      this.config.headers['X-API-TOKEN'] = this.config.API_TOKEN || this.config.headers['X-API-TOKEN'] || '';
    }

    this.LOGOUT_TYPES = {
      JUST_HERE: 'JUST_HERE',
      THIS_DEVICE: 'THIS_DEVICE',
      OTHER_DEVICES: 'OTHER_DEVICES',
      SSO_TOKEN: 'SSO_TOKEN',
      EVERYWHERE: 'EVERYWHERE'
    };

    this.EVENT_TYPES = {
      LAST_LOGIN: 'LAST_LOGIN_EVENT',
      LOGIN_FAIL: 'LOGIN_FAIL',
      LOGOUT: 'LOGOUT',
      PRODUCT_SHARE_INVITE: 'PRODUCT_SHARE_INVITE',
      PRODUCT_SHARE_ACCEPT: 'PRODUCT_SHARE_ACCEPT',
      PRODUCT_SHARE_REVOKE: 'PRODUCT_SHARE_REVOKE',
      GIFT_ARTICLE_TOKEN_CREATED: 'GIFT_ARTICLE_TOKEN_CREATED',
    };

    this.LOGIN_EVENT_FAILURE_MAPPING = {
      401: 'Invalid Credential',
      406: 'Migration Failed',
      412: 'Email Not Verified',
      300: 'Username and Email Mismatch',
      303: 'Non-email username',
      429: 'Concurrent Session Exceed'
    };
  }

  _sendAsForm(url, formData) {
    const headers = { 'Content-Type': 'application/x-www-form-urlencoded' };
    return {
      url,
      data: formData,
      headers,
    };
  }

  getLoginFailureCause(status) {
    return this.LOGIN_EVENT_FAILURE_MAPPING[status] || 'Unknown';
  }

  migrate(username, password, source = null) {
    const url = '/user/migrate';
    const data = {
      username,
      password
    };
    const params = {
      source
    };
    return this.post({ url, data, params });
  }

  login(username, password) {
    const url = 'login';
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    return this.post(this._sendAsForm(url, formData));
  }

  sendEvent(
    eventName = '',
    eventDetails = '',
    username = '',
    extraData = {}
  ) {
    const url = 'event';
    const data = {
      detail_type: eventName,
      cause: eventDetails,
      extra_data: extraData
    };
    const params = {
      username
    };
    return this.post({
      url,
      data,
      params,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )
    });
  }

  registerFreeUser(data, source = null) {
    const url = 'user/free';
    const params = {
      source
    };
    return this.post({ url, data, params });
  }

  registerFreeUserMvp(data, source = null) {
    const url = 'user/mvp/free';
    const params = {
      source
    };
    return this.post({ url, data, params });
  }

  resetPasswordStart(username) {
    const url = 'user/reset-password-start';
    const data = {
      username
    };
    return this.post({ url, data });
  }

  validateResetPasswordCode(username, code) {
    const url = 'user/reset-password-validate-code';
    const data = {
      username,
      code,
    };
    return this.post({ url, data });
  }

  resetPasswordComplete(username, password, code) {
    const url = 'user/reset-password-complete';
    const data = {
      username,
      password,
      code
    };
    return this.post({ url, data });
  }

  refreshUser() {
    const url = 'refresh-user';
    return this.post({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )
    });
  }

  cookies() {
    const url = 'cookies';
    return this.post({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        2,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )
    });
  }

  checkUserExists(username) {
    const url = 'user/username-exists';
    const params = {
      username
    };
    const extraInfo = {
      sendSuccessLog: false
    };
    return this.get({ url, params, extraInfo });
  }

  logout(logoutType) {
    const url = 'logout';
    const formData = new FormData();
    formData.append('logout_type', logoutType);
    return this.post(this._sendAsForm(url, formData));
  }

  resetUsername(oldUsername, newUsername, password) {
    const url = 'user/reset-username';
    const data = {
      old_username: oldUsername,
      new_username: newUsername,
      password
    };
    return this.post({ url, data });
  }

  checkUserLoggedInBefore(username) {
    const url = 'is-logged-in-before';
    const params = {
      username
    };
    const extraInfo = {
      sendSuccessLog: false
    };
    return this.get({ url, params, extraInfo });
  }

  hasConcurrencyLimitExceeded() {
    const blaizeSession = Utils.getCookie('blaize_session');
    const params = {
      _bs: Utils.btoa(blaizeSession)
    };
    const extraInfo = {
      sendSuccessLog: false
    };
    const url = 'is-concurrency-limit-exceed';
    return this.get({ url, params, extraInfo });
  }

  changeNcpPassword(username, newPassword) {
    const url = 'user/change-ncp-password';
    const data = {
      username,
      new_password: newPassword,
    };
    return this.put({ url, data });
  }

  registerExistingUser(data, source = null) {
    const url = 'user/register-existing-customer';
    const params = {
      source
    };
    return this.post({ url, data, params });
  }

  getCorporateSsoConfig() {
    const url = 'corporatesso/config';
    const extraInfo = {
      sendSuccessLog: false
    };
    return this.get({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        4,
        500,
        RequestResponseHelpers.anyErrorStatus()
      ),
      extraInfo,
    });
  }

  clearCookies() {
    const url = 'clear-cookies';
    return this.post({ url });
  }

  createCorporateSssoUser(username) {
    const url = 'corporatesso/create-and-connect-user';
    const data = {
      username
    };
    return this.put({ url, data });
  }

  corporateSsoOidcFinalize(zephrData) {
    const url = 'corporatesso/oidc-finalize';

    const userAttributes = zephrData?.userAttributes;
    const customAttributes = zephrData?.customAttributes;
    const firstName = userAttributes ? userAttributes['first-name'] : null;
    const lastName = userAttributes ? userAttributes['last-name'] : null;
    const countryCode = customAttributes ? customAttributes['country-code'] : null;

    const data = {
      first_name: firstName,
      last_name: lastName,
      country_code: countryCode
    };

    return this.post({
      url,
      data,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )
    });
  }

  corporateSsoOidcFinalizeContinue() {
    const url = 'corporatesso/oidc-finalize-continue';
    return this.post({
      url,
      'axios-retry': RequestResponseHelpers.getAxiosRetryConfig(
        3,
        2000,
        RequestResponseHelpers.get5xxStatuses()
      )
    });
  }

  deleteUnusedSession(username, browser) {
    const url = `delete-unused-sessions`;
    const params = {
      username,
      browser: browser || ''
    };
    return this.delete({ url, params });
  }

  updateLastSeen() {
    const url = `user/last-seen`;
    const extraInfo = {
      sendSuccessLog: false,
    };
    return this.put({ url, extraInfo });
  }

  shouldSendLogToServer(config, isError) {
    return Utils.shouldSendLogToServer(config, isError);
  }

  logInServer(level, message, client = 'SUB-AUTH-WEBAPP') {
    const url = `logging/${level}`;
    const extraInfo = {
      sendSuccessLog: false,
    };
    const data = {
      message,
      client,
    };
    return this.post({ url, data, extraInfo });
  }

  generateRequestConfigForSendingLogToServer(config) {
    const url = `logging/${config.level}`;
    const client = this.clientName;
    config.data = config.data || {};
    const data = {
      ...config.data,
      client,
      message: config.message
    };
    const newConfig = {
      ...config,
      url,
      data
    };
    return newConfig;
  }
}

export { NhstAuthClient };
